import { NavLink, useLocation, Link } from "react-router-dom";
import {
    LayoutDashboard, Users, FolderKanban, CheckSquare, Calendar, Wallet,
    FileText, FileSignature, BarChart3, UserCircle2, Building2, Sparkles, Settings,
    ChevronRight, Receipt, Camera, MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";
import Logo from "@/components/common/Logo";
import tr from "@/i18n/tr";
import { useAuth } from "@/context/AuthContext";
import {
    Avatar, AvatarFallback, AvatarImage,
} from "@/components/ui/avatar";

export const SIDEBAR_ITEMS = [
    { key: "dashboard",       label: tr.sidebar.dashboard,       to: "/dashboard",                 Icon: LayoutDashboard, active: true },
    { key: "clients",         label: tr.sidebar.clients,         to: "/customers",                 Icon: Users, active: true, roles: ["admin_manager_employee"] },
    { key: "projects",        label: tr.sidebar.projects,        to: "/projects",                  Icon: FolderKanban, active: true },
    { key: "tasks",           label: tr.sidebar.tasks,           to: "/tasks",                     Icon: CheckSquare, active: true },
    { key: "content-calendar",label: tr.sidebar.contentCalendar, to: "/content-calendar",          Icon: Calendar, active: true },
    { key: "shoot-planner",   label: tr.sidebar.shootPlanner,    to: "/shoot-planner",             Icon: Camera, active: true },
    { key: "finance",         label: tr.sidebar.finance,         to: "/finance",                   Icon: Wallet, active: true, adminOnly: true },
    { key: "expenses",        label: tr.sidebar.expenses,        to: "/expenses",                  Icon: Receipt, active: true },
    { key: "proposals",       label: tr.sidebar.proposals,       to: "/proposals",                 Icon: FileText, active: true, hideForEmployee: true },
    { key: "contracts",       label: tr.sidebar.contracts,       to: "/contracts",                 Icon: FileSignature, active: true, hideForEmployee: true },
    { key: "reports",         label: tr.sidebar.reports,         to: "/reports",                   Icon: BarChart3, active: true, hideForEmployee: true },
    { key: "employees",       label: tr.sidebar.employees,       to: "/personnel",                 Icon: UserCircle2, active: true, hideForEmployee: true },
    { key: "departments",     label: tr.sidebar.departments,     to: "/departments",               Icon: Building2, active: true, hideForEmployee: true },
    { key: "team-chat",       label: "Personel Sohbeti",         to: "/team-chat",                 Icon: MessageSquare, active: true },
    { key: "ai-assistant",    label: tr.sidebar.aiAssistant,     to: "/ai-assistant",              Icon: Sparkles, active: true },
    { key: "settings",        label: tr.sidebar.settings,        to: "/settings",                  Icon: Settings, active: true },
];

const ADMIN_ROLES = new Set(["super_admin", "admin", "agency_owner"]);
const FINANCE_ROLES = new Set(["super_admin", "admin", "agency_owner", "finance", "accountant"]);
const MANAGER_ROLES = new Set(["manager", "account_manager", "hr"]);
const EMPLOYEE_ROLES = new Set(["employee", "intern"]);

function isVisibleForUser(item, user) {
    if (!user) return false;
    const role = user.role;
    // Per-user menu_access override (set by admin/manager). null/undefined = role-based default.
    if (Array.isArray(user.menu_access)) {
        return user.menu_access.includes(item.key);
    }
    // Finance/Maliye — sadece finance yetkilileri ve admin
    if (item.key === "finance") return FINANCE_ROLES.has(role);
    // Admin-only marker (defensive; used only if adminOnly true and not covered above)
    if (item.adminOnly && !ADMIN_ROLES.has(role)) return false;
    // Employee restrictions: hide sensitive back-office items
    if (item.hideForEmployee && EMPLOYEE_ROLES.has(role)) return false;
    return true;
}

function initials(name) {
    if (!name) return "OMB";
    return name.split(" ").filter(Boolean).slice(0, 2).map(n => n[0]?.toUpperCase()).join("");
}

export default function Sidebar({ onNavigate }) {
    const { user } = useAuth();
    const location = useLocation();

    return (
        <aside className="flex h-full w-[260px] shrink-0 flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
            {/* Logo */}
            <div className="flex h-[68px] items-center px-5 border-b border-sidebar-border">
                <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="sidebar-logo">
                    <Logo variant="light" />
                </Link>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-4 space-y-0.5">
                {SIDEBAR_ITEMS.filter((item) => isVisibleForUser(item, user)).map(({ key, label, to, Icon, active }) => {
                    const isCurrent = location.pathname === to || (key !== "dashboard" && location.pathname.startsWith(to));
                    return (
                        <NavLink
                            key={key}
                            to={to}
                            onClick={onNavigate}
                            data-testid={`sidebar-item-${key}`}
                            className={({ isActive }) => cn(
                                "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13.5px] font-medium transition-all",
                                (isActive || isCurrent)
                                    ? "bg-[hsl(var(--brand))] text-white shadow-brand"
                                    : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground",
                                !active && "opacity-90"
                            )}
                        >
                            <Icon className="h-[18px] w-[18px] shrink-0" />
                            <span className="flex-1 truncate">{label}</span>
                            {!active && (
                                <ChevronRight className="h-3.5 w-3.5 opacity-40 group-hover:opacity-70" />
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {/* User */}
            <div className="border-t border-sidebar-border p-3">
                <Link
                    to="/profile"
                    onClick={onNavigate}
                    data-testid="sidebar-user"
                    className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-sidebar-hover transition-colors"
                >
                    <Avatar className="h-9 w-9 border border-sidebar-border">
                        <AvatarImage src={user?.avatar_url || undefined} alt={user?.full_name} />
                        <AvatarFallback className="bg-[hsl(var(--brand))] text-white text-[11px] font-bold">
                            {initials(user?.full_name)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-semibold text-sidebar-foreground">
                            {user?.full_name || "Kullanıcı"}
                        </div>
                        <div className="truncate text-[11px] text-sidebar-muted">
                            {user?.title || "Personel"}
                        </div>
                    </div>
                </Link>
            </div>
        </aside>
    );
}
